/**
 * The audit trail as a table: who, when, what — and what the entry carried.
 *
 * Shared by the per-dossier history (BRIEF surface 5) and the founder's
 * system-wide trail, because they are the same rows read with a different
 * filter. One renderer means one place where an action gets a human name and
 * one place that knows what a redacted entry looks like.
 *
 * A redacted entry is the normal outcome of a deletion, not an error: the row
 * survives, its payload does not. It is labelled in words ("Inhalt nach Ablauf
 * der Aufbewahrung entfernt"), never by being greyed out alone.
 */
import { actionLabel, describeDetail, formatMoment, isRedacted } from '@/features/audit/auditFormat'
import { useI18n } from '@/i18n'
import type { AuditEventRef } from '@/api/types'

export function AuditTable({
  events,
  caption,
  emptyLabel,
}: {
  events: readonly AuditEventRef[]
  caption: string
  emptyLabel?: string
}) {
  const { t } = useI18n()
  if (events.length === 0) {
    return (
      <p className="text-[13px] text-muted" data-testid="audit-empty">
        {emptyLabel ?? t('audit.empty')}
      </p>
    )
  }

  return (
    // A horizontally scrolling table has to be reachable by keyboard:
    // `tabIndex={0}` makes the scroll container focusable so the arrow keys can
    // move it, and the name stops that focus stop from being an unlabelled one.
    // WCAG 2.2 §2.1.1 — axe flags exactly this at 320 CSS px, where the table's
    // min-width forces the overflow. `group` and not `region`: `region` is a
    // landmark, and a second landmark carrying the same name as the <section>
    // around it is worse for a screen-reader listener than no landmark at all.
    <div className="overflow-x-auto" tabIndex={0} role="group" aria-label={caption}>
      <table className="w-full min-w-[640px] border-collapse text-left text-[13px]">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-border text-[12px] tracking-wide text-muted uppercase">
            <th scope="col" className="py-2 pe-4 font-medium">
              {t('audit.when')}
            </th>
            <th scope="col" className="py-2 pe-4 font-medium">
              {t('audit.what')}
            </th>
            <th scope="col" className="py-2 pe-4 font-medium">
              {t('audit.who')}
            </th>
            <th scope="col" className="py-2 font-medium">
              {t('audit.detail')}
            </th>
          </tr>
        </thead>
        <tbody data-testid="audit-rows">
          {events.map((event) => {
            const detail = (event.detail ?? {}) as Record<string, unknown>
            return (
              <tr key={event.id} className="border-b border-border align-top last:border-b-0">
                <td className="num py-2 pe-4 whitespace-nowrap text-muted">
                  {formatMoment(event.created_at)}
                </td>
                <td className="py-2 pe-4 font-medium text-ink">
                  {actionLabel(event.action, t)}
                </td>
                <td className="py-2 pe-4 text-muted">{event.actor_email ?? t('audit.system')}</td>
                <td className="py-2 text-muted">
                  {isRedacted(detail) ? (
                    <span data-testid="audit-redacted">{t('audit.redacted')}</span>
                  ) : (
                    <span className="num break-all">{describeDetail(detail)}</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
